import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import KodikApiService from '../../services/KodikApiService';
import { normalizeKodikSrc } from '../../components/player/playerSources';
import placeholderCover from '../../assets/card_placeholder.png';
import AdminLayout from './AdminLayout';
import './AdminPage.css';

const PRIMARY_SEARCH_FIELDS = [
    ['title', 'Название'],
    ['titleOrig', 'Оригинальное название'],
    ['kodikId', 'Kodik ID'],
    ['year', 'Год'],
];

const ADVANCED_SEARCH_FIELDS = [
    ['playerLink', 'Ссылка плеера'],
    ['kinopoiskId', 'Кинопоиск ID'],
    ['imdbId', 'IMDb ID'],
    ['mdlId', 'MyDramaList ID'],
    ['worldartAnimationId', 'World Art ID'],
    ['worldartLink', 'World Art ссылка'],
    ['shikimoriId', 'Shikimori ID'],
    ['types', 'Типы'],
    ['translationId', 'ID перевода'],
    ['translationType', 'Тип перевода'],
    ['season', 'Сезон'],
    ['episode', 'Серия'],
];

const BOOLEAN_SEARCH_FIELDS = [
    ['strict', 'Строгий поиск'],
    ['fullMatch', 'Полное совпадение'],
    ['withEpisodesData', 'Данные серий'],
    ['withMaterialData', 'Данные материала'],
];

const TERMINAL_STATUSES = new Set(['COMPLETED', 'FAILED']);
const initialSearch = { limit: '20' };
const STATUS_LABELS = {
    MATCHED: 'Сопоставлено',
    AMBIGUOUS: 'Нужен выбор',
    NOT_FOUND: 'Не найдено',
    PENDING: 'Ожидает обработки',
    MANUAL: 'Связано вручную',
    QUEUED: 'В очереди',
    RUNNING: 'Выполняется',
    COMPLETED: 'Завершено',
    FAILED: 'Ошибка',
};
const MATCH_SIGNAL_LABELS = {
    title_exact: 'точное название',
    title_fuzzy: 'похожее название',
    year: 'совпадает год',
    year_conflict: 'год отличается',
    studio: 'совпадает студия',
    type: 'совпадает формат',
    no_strong_signals: 'нет надёжных признаков',
    no_candidates: 'кандидатов нет',
};
const SYNC_MODES = [
    {
        value: 'INCREMENTAL',
        label: 'Обычное обновление',
        description: 'Загрузить новые и изменённые материалы Kodik. Используйте в большинстве случаев.',
    },
    {
        value: 'RETRY_FAILED',
        label: 'Повторить ошибки',
        description: 'Повторно обработать материалы, которые не удалось загрузить ранее.',
    },
    {
        value: 'FULL',
        label: 'Полная пересборка',
        description: 'Заново прочитать весь каталог. Операция долгая и создаёт повышенную нагрузку.',
    },
];
const CATALOG_STATUSES = ['MATCHED', 'MANUAL', 'AMBIGUOUS', 'NOT_FOUND', 'PENDING'];
const CATALOG_SORTS = [
    { value: 'updatedAt', label: 'Недавно изменённые' },
    { value: 'name', label: 'По названию' },
    { value: 'animixId', label: 'По Animix ID' },
];
const EMPTY_PART_FORM = {
    partId: null,
    logicalSeason: '',
    kodikId: '',
    sourceSeason: '',
    sourceEpisodeStart: '',
    sourceEpisodeEnd: '',
    targetEpisodeStart: '',
};

const getErrorMessage = (error) => {
    if (error?.code === 'SESSION_REQUIRED' || error?.status === 401) return 'Сессия истекла. Войдите в аккаунт снова.';
    if (error?.status === 403) return 'Для этой операции нужны права модератора.';
    if (error?.code === 'SHIKIMORI_KODIK_MISMATCH') return 'Выбранный Kodik ID не относится к указанному Shikimori ID.';
    if (error?.code === 'ANIME_ALREADY_MATCHED') return 'Аниме уже успешно сопоставлено. Для замены используйте ручной выбор кандидата.';
    if (error?.code === 'SOURCE_SEASON_REQUIRED') return 'У выбранного материала несколько сезонов. Выберите исходный сезон Kodik.';
    if (error?.code === 'SOURCE_SEASON_NOT_FOUND') return 'Указанного исходного сезона нет в выбранном материале Kodik.';
    if (error?.code === 'SOURCE_SEASON_NOT_APPLICABLE') return 'Для фильма исходный сезон указывать не нужно.';
    if (error?.code === 'KODIK_EPISODES_NOT_FOUND') return 'В выбранном сериале Kodik не найдены серии.';
    if (error?.code === 'KODIK_PART_ALREADY_ASSIGNED') return 'Этот Kodik ID уже назначен другому логическому сезону.';
    if (error?.code === 'EPISODE_MAPPING_CONFLICT') {
        const episodes = Array.isArray(error.details?.overlappingEpisodes) ? error.details.overlappingEpisodes.join(', ') : '';
        return episodes
            ? `Сегмент пересекается с существующей нумерацией сезона ${error.details?.season}: серии ${episodes}.`
            : 'Сегмент пересекается с существующей нумерацией серий.';
    }
    if (error?.code === 'LOGICAL_SEASON_NOT_FOUND') return 'Такая привязка сезона уже отсутствует.';
    return error?.message || 'Не удалось выполнить запрос.';
};

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
    return String(value);
};

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatMatchMethod = (method) => String(method || '')
    .split('+')
    .filter(Boolean)
    .map((signal) => MATCH_SIGNAL_LABELS[signal] || signal)
    .join(', ') || 'Причина оценки не указана';

const StatusBadge = ({ status }) => (
    <span className={`kodik-status is-${String(status || 'unknown').toLowerCase()}`} title={status || 'UNKNOWN'}>
        {STATUS_LABELS[status] || status || 'Неизвестно'}
    </span>
);

StatusBadge.propTypes = {
    status: PropTypes.string,
};

const Translation = ({ translation }) => (
    <span>{translation?.title || 'Перевод не указан'}{translation?.id ? ` · ${translation.id}` : ''}</span>
);

Translation.propTypes = {
    translation: PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string,
    }),
};

const KodikModerationPage = () => {
    const animeIdInputRef = useRef(null);
    const targetSectionRef = useRef(null);
    const partsSectionRef = useRef(null);
    const shikimoriPanelRef = useRef(null);
    const [activeView, setActiveView] = useState('match');
    const [openedFromCatalog, setOpenedFromCatalog] = useState(false);
    const [animeId, setAnimeId] = useState('');
    const [reason, setReason] = useState('Ручное сопоставление в админ-панели');
    const [candidatesData, setCandidatesData] = useState(null);
    const [candidateLoading, setCandidateLoading] = useState(false);
    const [candidateError, setCandidateError] = useState('');
    const [candidateNotice, setCandidateNotice] = useState('');
    const [busyKodikId, setBusyKodikId] = useState('');
    const [searchForm, setSearchForm] = useState(initialSearch);
    const [searchData, setSearchData] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [searchNotice, setSearchNotice] = useState('');
    const [rematch, setRematch] = useState(null);
    const [rematchId, setRematchId] = useState('');
    const [rematchLoading, setRematchLoading] = useState(false);
    const [syncMode, setSyncMode] = useState('INCREMENTAL');
    const [syncRun, setSyncRun] = useState(null);
    const [syncRunId, setSyncRunId] = useState('');
    const [syncLoading, setSyncLoading] = useState(false);
    const [operationsError, setOperationsError] = useState('');
    const [playerPreview, setPlayerPreview] = useState(null);
    const [previewLoadingId, setPreviewLoadingId] = useState('');
    const [conflictPage, setConflictPage] = useState(0);
    const [conflictData, setConflictData] = useState(null);
    const [conflictLoading, setConflictLoading] = useState(false);
    const [conflictError, setConflictError] = useState('');
    const [shikimoriId, setShikimoriId] = useState('');
    const [shikimoriMode, setShikimoriMode] = useState('auto');
    const [shikimoriKodikId, setShikimoriKodikId] = useState('');
    const [shikimoriLoading, setShikimoriLoading] = useState(false);
    const [shikimoriResult, setShikimoriResult] = useState(null);
    const [partsData, setPartsData] = useState(null);
    const [partsLoading, setPartsLoading] = useState(false);
    const [partsError, setPartsError] = useState('');
    const [partsNotice, setPartsNotice] = useState('');
    const [partForm, setPartForm] = useState(EMPTY_PART_FORM);
    const [availableSourceSeasons, setAvailableSourceSeasons] = useState([]);
    const [busyPartId, setBusyPartId] = useState('');
    const [catalogPage, setCatalogPage] = useState(0);
    const [catalogData, setCatalogData] = useState(null);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [catalogError, setCatalogError] = useState('');
    const [catalogQuery, setCatalogQuery] = useState('');
    const [catalogAppliedQuery, setCatalogAppliedQuery] = useState('');
    const [catalogStatuses, setCatalogStatuses] = useState([]);
    const [catalogSort, setCatalogSort] = useState('updatedAt');
    const [catalogDirection, setCatalogDirection] = useState('desc');
    const [catalogReloadToken, setCatalogReloadToken] = useState(0);

    const candidates = useMemo(() => [...(candidatesData?.candidates || [])].sort((left, right) => {
        if (Boolean(left.primary) !== Boolean(right.primary)) return left.primary ? -1 : 1;
        if (Boolean(left.manualLocked) !== Boolean(right.manualLocked)) return left.manualLocked ? -1 : 1;
        return Number(right.score || 0) - Number(left.score || 0);
    }), [candidatesData]);
    const searchItems = useMemo(() => searchData?.items || [], [searchData]);
    const conflicts = useMemo(() => conflictData?.items || [], [conflictData]);
    const parts = useMemo(() => [...(partsData?.items || [])].sort((left, right) => (
        Number(left.season || 0) - Number(right.season || 0)
        || Number(left.targetEpisodeStart || 0) - Number(right.targetEpisodeStart || 0)
    )), [partsData]);
    const partMappingPreview = useMemo(() => {
        const logicalSeason = Number(partForm.logicalSeason);
        const sourceStart = Number(partForm.sourceEpisodeStart);
        const sourceEnd = Number(partForm.sourceEpisodeEnd);
        const targetStart = Number(partForm.targetEpisodeStart);
        if (!Number.isInteger(logicalSeason) || !Number.isInteger(targetStart) || targetStart <= 0) return '';

        if (!Number.isInteger(sourceStart) || sourceStart <= 0) {
            return `Фильм Kodik → сезон ${logicalSeason}, серия ${targetStart} в Animix`;
        }

        const targetEnd = Number.isInteger(sourceEnd) && sourceEnd >= sourceStart
            ? targetStart + sourceEnd - sourceStart
            : null;
        return `Kodik: серии ${sourceStart}–${targetEnd ? sourceEnd : 'до конца'} → Animix: сезон ${logicalSeason}, серии ${targetStart}–${targetEnd || 'до конца'}`;
    }, [partForm]);
    const catalogItems = useMemo(() => catalogData?.items || [], [catalogData]);

    useEffect(() => {
        document.title = 'Kodik — админ-панель';
    }, []);

    useEffect(() => {
        let cancelled = false;
        setConflictLoading(true);
        setConflictError('');
        KodikApiService.getConflicts(conflictPage, 50)
            .then((data) => {
                if (!cancelled) setConflictData(data);
            })
            .catch((error) => {
                if (!cancelled) setConflictError(getErrorMessage(error));
            })
            .finally(() => {
                if (!cancelled) setConflictLoading(false);
            });
        return () => { cancelled = true; };
    }, [conflictPage]);

    useEffect(() => {
        if (activeView !== 'catalog') return undefined;
        let cancelled = false;
        setCatalogLoading(true);
        setCatalogError('');
        KodikApiService.getAnimeIntegrations({
            page: catalogPage,
            size: 50,
            query: catalogAppliedQuery,
            status: catalogStatuses,
            sort: catalogSort,
            direction: catalogDirection,
        })
            .then((data) => {
                if (!cancelled) setCatalogData(data);
            })
            .catch((error) => {
                if (!cancelled) setCatalogError(getErrorMessage(error));
            })
            .finally(() => {
                if (!cancelled) setCatalogLoading(false);
            });
        return () => { cancelled = true; };
    }, [activeView, catalogAppliedQuery, catalogDirection, catalogPage, catalogReloadToken, catalogSort, catalogStatuses]);

    useEffect(() => {
        if (!playerPreview) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setPlayerPreview(null);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [playerPreview]);

    useEffect(() => {
        if (!rematchId || TERMINAL_STATUSES.has(rematch?.status)) return undefined;
        const timer = setInterval(async () => {
            try {
                const data = await KodikApiService.getRematchStatus(rematchId);
                setRematch(data);
            } catch (error) {
                setOperationsError(getErrorMessage(error));
            }
        }, 2500);
        return () => clearInterval(timer);
    }, [rematch?.status, rematchId]);

    useEffect(() => {
        if (!syncRunId || TERMINAL_STATUSES.has(syncRun?.status)) return undefined;
        const timer = setInterval(async () => {
            try {
                const data = await KodikApiService.getSyncStatus(syncRunId);
                setSyncRun(data);
            } catch (error) {
                setOperationsError(getErrorMessage(error));
            }
        }, 2500);
        return () => clearInterval(timer);
    }, [syncRun?.status, syncRunId]);

    const requireAnimeId = (setContextError = setCandidateError) => {
        const id = Number(animeId);
        if (!Number.isInteger(id) || id <= 0) {
            setContextError('Сначала укажите Animix ID, к которому нужно привязать материал.');
            animeIdInputRef.current?.focus();
            return null;
        }
        return id;
    };

    const handleAnimeIdChange = (event) => {
        setAnimeId(event.target.value);
        setCandidatesData(null);
        setCandidateError('');
        setCandidateNotice('');
        setSearchNotice('');
        setShikimoriId('');
        setShikimoriKodikId('');
        setShikimoriResult(null);
        setPartsData(null);
        setPartsError('');
        setPartsNotice('');
        setPartForm(EMPTY_PART_FORM);
        setAvailableSourceSeasons([]);
    };

    const loadCandidatesById = async (id, refresh = false) => {
        setCandidateLoading(true);
        setCandidateError('');
        setCandidateNotice('');
        try {
            const data = await KodikApiService.getCandidates(id, { refresh });
            setCandidatesData(data);
            setShikimoriId(data.shikimoriId || '');
            return data;
        } catch (error) {
            setCandidateError(getErrorMessage(error));
            return null;
        } finally {
            setCandidateLoading(false);
        }
    };

    const loadCandidates = async (refresh = false) => {
        const id = requireAnimeId();
        if (!id) return null;
        const [candidateData] = await Promise.all([
            loadCandidatesById(id, refresh),
            loadPartsById(id),
        ]);
        return candidateData;
    };

    const loadPartsById = async (id) => {
        setPartsLoading(true);
        setPartsError('');
        try {
            const data = await KodikApiService.getParts(id);
            setPartsData(data);
            return data;
        } catch (error) {
            setPartsError(getErrorMessage(error));
            return null;
        } finally {
            setPartsLoading(false);
        }
    };

    const refreshConflicts = async () => {
        setConflictLoading(true);
        setConflictError('');
        try {
            setConflictData(await KodikApiService.getConflicts(conflictPage, 50));
        } catch (error) {
            setConflictError(getErrorMessage(error));
        } finally {
            setConflictLoading(false);
        }
    };

    const selectConflict = async (conflict) => {
        const id = String(conflict.animixAnimeId);
        setOpenedFromCatalog(false);
        setActiveView('match');
        setAnimeId(id);
        setCandidatesData(null);
        setCandidateError('');
        setCandidateNotice('');
        setShikimoriId(conflict.shikimoriId || '');
        setShikimoriMode('auto');
        setShikimoriKodikId('');
        setShikimoriResult(null);
        requestAnimationFrame(() => targetSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        await Promise.all([loadCandidatesById(Number(id), false), loadPartsById(Number(id))]);
    };

    const openCatalogAnime = async (item) => {
        const id = String(item.animixAnimeId);
        setOpenedFromCatalog(true);
        setActiveView('match');
        setAnimeId(id);
        setCandidatesData(null);
        setCandidateError('');
        setCandidateNotice('');
        setShikimoriId(item.shikimoriId || '');
        setShikimoriMode('auto');
        setShikimoriKodikId('');
        setShikimoriResult(null);
        requestAnimationFrame(() => targetSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        await Promise.all([loadCandidatesById(Number(id), false), loadPartsById(Number(id))]);
    };

    const submitCatalogSearch = (event) => {
        event.preventDefault();
        setCatalogPage(0);
        setCatalogAppliedQuery(catalogQuery.trim());
        setCatalogReloadToken((value) => value + 1);
    };

    const toggleCatalogStatus = (status) => {
        setCatalogPage(0);
        setCatalogStatuses((current) => (
            current.includes(status)
                ? current.filter((item) => item !== status)
                : [...current, status]
        ));
    };

    const resetCatalogFilters = () => {
        setCatalogQuery('');
        setCatalogAppliedQuery('');
        setCatalogStatuses([]);
        setCatalogSort('updatedAt');
        setCatalogDirection('desc');
        setCatalogPage(0);
        setCatalogReloadToken((value) => value + 1);
    };

    const changeConflictPage = (nextPage) => {
        setConflictPage(Math.max(0, nextPage));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const changeCatalogPage = (nextPage) => {
        setCatalogPage(Math.max(0, nextPage));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const submitShikimoriMatch = async () => {
        const id = requireAnimeId();
        if (!id) return;
        const normalizedShikimoriId = shikimoriId.trim();
        if (!/^[1-9][0-9]{0,18}$/.test(normalizedShikimoriId)) {
            setCandidateError('Shikimori ID должен быть положительным числом.');
            return;
        }
        if (shikimoriMode === 'explicit' && !shikimoriKodikId.trim()) {
            setCandidateError('Укажите Kodik ID конкретного перевода.');
            return;
        }
        setShikimoriLoading(true);
        setCandidateError('');
        setCandidateNotice('');
        setShikimoriResult(null);
        try {
            const result = await KodikApiService.setShikimoriMatch(id, normalizedShikimoriId, {
                kodikId: shikimoriMode === 'explicit' ? shikimoriKodikId.trim() : null,
                reason,
            });
            setShikimoriResult(result);
            await Promise.all([loadCandidatesById(id, false), loadPartsById(id), refreshConflicts()]);
        } catch (error) {
            setCandidateError(getErrorMessage(error));
        } finally {
            setShikimoriLoading(false);
        }
    };

    const chooseCandidateShikimori = (candidate) => {
        setActiveView('match');
        setShikimoriId(candidate.shikimoriId || '');
        setShikimoriKodikId(candidate.kodikId);
        setShikimoriMode('explicit');
        setShikimoriResult(null);
        requestAnimationFrame(() => {
            if (shikimoriPanelRef.current) shikimoriPanelRef.current.open = true;
            shikimoriPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    };

    const setManualMatch = async (kodikId, errorContext = 'candidates') => {
        const setContextError = errorContext === 'search' ? setSearchError : setCandidateError;
        const id = requireAnimeId(setContextError);
        if (!id) return;
        setBusyKodikId(kodikId);
        setContextError('');
        try {
            await KodikApiService.setMatch(id, kodikId, reason);
            await Promise.all([loadCandidatesById(id, false), loadPartsById(id), refreshConflicts()]);
            const notice = `Материал ${kodikId} теперь основной для Animix ID ${id}.`;
            if (errorContext === 'search') setSearchNotice(notice);
            else setCandidateNotice(notice);
        } catch (error) {
            setContextError(getErrorMessage(error));
        } finally {
            setBusyKodikId('');
        }
    };

    const deleteManualMatch = async (kodikId) => {
        const id = requireAnimeId();
        if (!id || !window.confirm(`Удалить ручное сопоставление ${kodikId}?`)) return;
        setBusyKodikId(kodikId);
        setCandidateError('');
        try {
            await KodikApiService.deleteMatch(id, kodikId);
            await Promise.all([loadCandidatesById(id, false), loadPartsById(id), refreshConflicts()]);
        } catch (error) {
            setCandidateError(getErrorMessage(error));
        } finally {
            setBusyKodikId('');
        }
    };

    const runSearch = async (event) => {
        event.preventDefault();
        setSearchError('');
        setSearchNotice('');
        const hasCriteria = [...PRIMARY_SEARCH_FIELDS, ...ADVANCED_SEARCH_FIELDS]
            .some(([key]) => String(searchForm[key] || '').trim());
        if (!hasCriteria) {
            setSearchError('Укажите хотя бы название, Kodik ID или другой параметр поиска.');
            return;
        }
        setSearchLoading(true);
        try {
            const params = { ...searchForm };
            BOOLEAN_SEARCH_FIELDS.forEach(([key]) => {
                if (params[key] === true) params[key] = 'true';
                if (!params[key]) delete params[key];
            });
            setSearchData(await KodikApiService.search(params));
        } catch (error) {
            setSearchError(getErrorMessage(error));
        } finally {
            setSearchLoading(false);
        }
    };

    const getNextLogicalSeason = () => {
        const usedSeasons = new Set(parts.map((part) => Number(part.season)));
        let season = 1;
        while (usedSeasons.has(season)) season += 1;
        return season;
    };

    const getNextTargetEpisode = (logicalSeason) => {
        const lastEpisode = parts
            .filter((part) => Number(part.season) === Number(logicalSeason))
            .reduce((maximum, part) => Math.max(maximum, Number(part.targetEpisodeEnd || part.targetEpisodeStart || 0)), 0);
        return lastEpisode + 1;
    };

    const preparePart = (item) => {
        const logicalSeason = Number(item.logicalSeason || item.season || getNextLogicalSeason());
        setActiveView('parts');
        setPartForm({
            partId: item.partId || null,
            logicalSeason: String(logicalSeason),
            kodikId: item.kodikId || '',
            sourceSeason: item.sourceSeason ? String(item.sourceSeason) : '',
            sourceEpisodeStart: item.sourceEpisodeStart ? String(item.sourceEpisodeStart) : '',
            sourceEpisodeEnd: item.sourceEpisodeEnd ? String(item.sourceEpisodeEnd) : '',
            targetEpisodeStart: String(item.targetEpisodeStart || getNextTargetEpisode(logicalSeason)),
        });
        setAvailableSourceSeasons([]);
        setPartsError('');
        setPartsNotice('');
        requestAnimationFrame(() => partsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    };

    const savePart = async (event) => {
        event.preventDefault();
        const id = requireAnimeId(setPartsError);
        if (!id) return;
        const logicalSeason = Number(partForm.logicalSeason);
        const sourceSeason = partForm.sourceSeason === '' ? null : Number(partForm.sourceSeason);
        const sourceEpisodeStart = partForm.sourceEpisodeStart === '' ? null : Number(partForm.sourceEpisodeStart);
        const sourceEpisodeEnd = partForm.sourceEpisodeEnd === '' ? null : Number(partForm.sourceEpisodeEnd);
        const targetEpisodeStart = Number(partForm.targetEpisodeStart);
        if (!Number.isInteger(logicalSeason) || logicalSeason <= 0) {
            setPartsError('Логический сезон должен быть положительным числом.');
            return;
        }
        if (!partForm.kodikId.trim()) {
            setPartsError('Укажите Kodik ID материала для этого сезона.');
            return;
        }
        if (sourceSeason !== null && (!Number.isInteger(sourceSeason) || sourceSeason <= 0)) {
            setPartsError('Исходный сезон должен быть положительным числом.');
            return;
        }
        if (sourceEpisodeStart !== null && (!Number.isInteger(sourceEpisodeStart) || sourceEpisodeStart <= 0)) {
            setPartsError('Начальная серия Kodik должна быть положительным числом.');
            return;
        }
        if (sourceEpisodeEnd !== null && (!Number.isInteger(sourceEpisodeEnd) || sourceEpisodeEnd <= 0)) {
            setPartsError('Конечная серия Kodik должна быть положительным числом.');
            return;
        }
        if (sourceEpisodeStart === null && sourceEpisodeEnd !== null) {
            setPartsError('Сначала укажите начальную серию Kodik. Конечную можно оставить пустой, чтобы взять все оставшиеся серии.');
            return;
        }
        if (sourceEpisodeStart !== null && sourceEpisodeEnd < sourceEpisodeStart) {
            setPartsError('Конечная серия Kodik не может быть меньше начальной.');
            return;
        }
        if (!Number.isInteger(targetEpisodeStart) || targetEpisodeStart <= 0) {
            setPartsError('Начальная серия Animix должна быть положительным числом.');
            return;
        }

        setBusyPartId(String(partForm.partId || 'new'));
        setPartsError('');
        setPartsNotice('');
        setAvailableSourceSeasons([]);
        try {
            const payload = {
                logicalSeason,
                kodikId: partForm.kodikId.trim(),
                sourceSeason,
                sourceEpisodeStart,
                sourceEpisodeEnd,
                targetEpisodeStart,
                reason,
            };
            if (partForm.partId) {
                await KodikApiService.updatePartSegment(id, partForm.partId, payload);
            } else {
                await KodikApiService.createPartSegment(id, payload);
            }
            await Promise.all([loadPartsById(id), loadCandidatesById(id, false)]);
            setPartsNotice(partForm.partId ? `Сегмент #${partForm.partId} обновлён.` : `Сегмент сезона ${logicalSeason} добавлен.`);
            setPartForm(EMPTY_PART_FORM);
        } catch (error) {
            if (error?.code === 'SOURCE_SEASON_REQUIRED') {
                setAvailableSourceSeasons(
                    (Array.isArray(error.details?.availableSourceSeasons) ? error.details.availableSourceSeasons : [])
                        .map(Number)
                        .filter((season) => Number.isInteger(season) && season > 0)
                );
            }
            setPartsError(getErrorMessage(error));
        } finally {
            setBusyPartId('');
        }
    };

    const deletePart = async (part) => {
        const id = requireAnimeId(setPartsError);
        if (!id || !part.partId || !window.confirm(`Удалить сегмент #${part.partId} сезона ${part.season} (${part.kodikId})?`)) return;
        setBusyPartId(String(part.partId));
        setPartsError('');
        setPartsNotice('');
        try {
            await KodikApiService.deletePartSegment(id, part.partId);
            await Promise.all([loadPartsById(id), loadCandidatesById(id, false)]);
            setPartsNotice(`Сегмент #${part.partId} удалён.`);
        } catch (error) {
            setPartsError(getErrorMessage(error));
        } finally {
            setBusyPartId('');
        }
    };

    const startRematch = async () => {
        const id = requireAnimeId();
        if (!id) return;
        setRematchLoading(true);
        setOperationsError('');
        try {
            const accepted = await KodikApiService.startRematch(id);
            setRematchId(accepted.taskId);
            setRematch(accepted);
        } catch (error) {
            setOperationsError(getErrorMessage(error));
        } finally {
            setRematchLoading(false);
        }
    };

    const refreshRematch = async () => {
        if (!rematchId.trim()) return;
        setRematchLoading(true);
        setOperationsError('');
        try {
            setRematch(await KodikApiService.getRematchStatus(rematchId.trim()));
        } catch (error) {
            setOperationsError(getErrorMessage(error));
        } finally {
            setRematchLoading(false);
        }
    };

    const startSync = async () => {
        if (syncMode === 'FULL' && !window.confirm('Полностью перечитать каталог Kodik? Эта операция может выполняться долго.')) return;
        setSyncLoading(true);
        setOperationsError('');
        try {
            const accepted = await KodikApiService.startSync(syncMode);
            setSyncRunId(accepted.runId);
            setSyncRun(accepted);
        } catch (error) {
            setOperationsError(getErrorMessage(error));
            if (error?.code === 'SYNC_ALREADY_RUNNING' && error?.details?.runId) {
                setSyncRunId(error.details.runId);
            }
        } finally {
            setSyncLoading(false);
        }
    };

    const refreshSync = async () => {
        if (!syncRunId.trim()) return;
        setSyncLoading(true);
        setOperationsError('');
        try {
            setSyncRun(await KodikApiService.getSyncStatus(syncRunId.trim()));
        } catch (error) {
            setOperationsError(getErrorMessage(error));
        } finally {
            setSyncLoading(false);
        }
    };

    const updateSearch = (key, value) => setSearchForm((current) => ({ ...current, [key]: value }));
    const selectedSyncMode = SYNC_MODES.find((mode) => mode.value === syncMode) || SYNC_MODES[0];
    const openPlayerPreview = async (item, errorContext = 'search') => {
        const setContextError = errorContext === 'candidates' ? setCandidateError : setSearchError;
        setContextError('');
        setPreviewLoadingId(item.kodikId);
        try {
            let previewItem = item;
            let url = normalizeKodikSrc(previewItem.playerUrl || previewItem.playerLink);
            if (!url) {
                const result = await KodikApiService.search({ kodikId: item.kodikId, strict: 'true', limit: 1 });
                previewItem = (result?.items || []).find((candidate) => candidate.kodikId === item.kodikId) || null;
                url = normalizeKodikSrc(previewItem?.playerUrl || previewItem?.playerLink);
            }
            if (!url) throw new Error('Kodik не вернул ссылку плеера для этого результата.');
            setPlayerPreview({
                url,
                title: previewItem?.title || previewItem?.titleOriginal || item.title || item.titleOriginal || item.kodikId,
                kodikId: item.kodikId,
            });
        } catch (error) {
            setContextError(getErrorMessage(error));
        } finally {
            setPreviewLoadingId('');
        }
    };

    return (
        <AdminLayout
            mainClassName="admin-list-shell kodik-admin-shell"
            eyebrow="Интеграции"
            title="Kodik"
            lead="Привязка материалов Kodik к аниме Animix."
        >
            <div className="ui-chips kodik-page-tabs" role="tablist" aria-label="Разделы интеграции Kodik">
                <button type="button" role="tab" aria-selected={activeView === 'match'} className="ui-chip" onClick={() => {
                    setOpenedFromCatalog(false);
                    setActiveView('match');
                }}>
                    Сопоставление <span>{conflictData?.total ?? '—'}</span>
                </button>
                <button type="button" role="tab" aria-selected={activeView === 'catalog'} className="ui-chip" onClick={() => setActiveView('catalog')}>
                    Все аниме {catalogData?.total != null ? <span>{catalogData.total}</span> : null}
                </button>
                <button type="button" role="tab" aria-selected={activeView === 'parts'} className="ui-chip" onClick={() => setActiveView('parts')}>
                    Сегменты {parts.length ? <span>{parts.length}</span> : null}
                </button>
                <button type="button" role="tab" aria-selected={activeView === 'search'} className="ui-chip" onClick={() => setActiveView('search')}>Ручной поиск</button>
                <button type="button" role="tab" aria-selected={activeView === 'operations'} className="ui-chip" onClick={() => setActiveView('operations')}>Обслуживание</button>
            </div>

            {activeView === 'match' ? (
                <div className={`kodik-match-layout${openedFromCatalog ? ' is-catalog-entity' : ''}`} ref={targetSectionRef} role="tabpanel">
                    {!openedFromCatalog ? <aside className="kodik-queue-pane">
                        <div className="kodik-queue-heading">
                            <div>
                                <h2>Очередь</h2>
                                <span>{conflictData?.total ?? '—'} осталось</span>
                            </div>
                            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={refreshConflicts} disabled={conflictLoading}>Обновить</button>
                        </div>
                        {conflictError ? <p className="kodik-feedback is-error">{conflictError}</p> : null}
                        <div className="kodik-conflict-list" aria-busy={conflictLoading}>
                            {conflicts.map((conflict) => (
                                <button
                                    type="button"
                                    className={`kodik-conflict-row${String(animeId) === String(conflict.animixAnimeId) ? ' is-selected' : ''}`}
                                    key={conflict.animixAnimeId}
                                    onClick={() => selectConflict(conflict)}
                                >
                                    <img src={conflict.thumb || placeholderCover} onError={(event) => { event.currentTarget.src = placeholderCover; }} alt="" />
                                    <span className="kodik-conflict-title">
                                        <strong>{conflict.name}</strong>
                                        <small>ID {conflict.animixAnimeId} · {formatValue(conflict.parsedYear)}</small>
                                    </span>
                                    <span className="kodik-conflict-meta">
                                        <StatusBadge status={conflict.status} />
                                        <small>{conflict.candidateCount} кандидатов</small>
                                    </span>
                                </button>
                            ))}
                            {!conflictLoading && !conflicts.length && !conflictError ? <p className="kodik-empty">Очередь пуста.</p> : null}
                            {conflictLoading && !conflicts.length ? <p className="kodik-empty">Загружаем очередь…</p> : null}
                        </div>
                        <div className="kodik-conflict-pagination">
                            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => changeConflictPage(conflictPage - 1)} disabled={conflictLoading || conflictPage === 0}>Назад</button>
                            <span>{conflictPage + 1} / {Math.max(conflictData?.totalPages || 1, 1)}</span>
                            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => changeConflictPage(conflictPage + 1)} disabled={conflictLoading || conflictPage + 1 >= (conflictData?.totalPages || 1)}>Дальше</button>
                        </div>
                    </aside> : null}

                    <section className="kodik-match-workspace">
                        <div className="kodik-section-heading">
                            <div>
                                <h2>{candidatesData?.name || 'Выберите аниме'}</h2>
                                <p>{candidatesData ? `Animix ID ${candidatesData.animixAnimeId} · ${formatValue(candidatesData.parsedYear)} · ${formatValue(candidatesData.studio)}` : openedFromCatalog ? 'Загружаем настройки выбранной сущности.' : 'Выберите запись из очереди или загрузите её по Animix ID.'}</p>
                            </div>
                            {openedFromCatalog ? <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => setActiveView('catalog')}>Вернуться к каталогу</button> : null}
                        </div>
                        <div className="kodik-target-bar">
                            <label className="kodik-field is-id-field">
                                <span>Animix ID</span>
                                <input ref={animeIdInputRef} type="number" min="1" value={animeId} onChange={handleAnimeIdChange} placeholder="504" />
                            </label>
                            <label className="kodik-field">
                                <span>Комментарий для истории</span>
                                <input value={reason} onChange={(event) => setReason(event.target.value)} maxLength="500" />
                            </label>
                            <button type="button" className="ui-btn ui-btn--sm admin-button" onClick={() => loadCandidates(false)} disabled={candidateLoading}>{candidateLoading ? 'Загрузка…' : 'Загрузить'}</button>
                            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => loadCandidates(true)} disabled={candidateLoading}>Обновить кандидатов</button>
                        </div>
                        {candidateError ? <p className="kodik-feedback is-error">{candidateError}</p> : null}
                        {candidateNotice ? <p className="kodik-feedback is-success">{candidateNotice}</p> : null}

                        {candidatesData ? (
                            <div className="kodik-candidate-toolbar">
                                <span><strong>{candidates.length}</strong> кандидатов</span>
                                <span>Shikimori: {formatValue(candidatesData.shikimoriId)}</span>
                                <button type="button" onClick={() => setActiveView('search')}>Нет нужного варианта? Ручной поиск</button>
                            </div>
                        ) : null}

                        <div className="kodik-results is-candidates">
                            {candidates.map((candidate, index) => (
                                <article className={`kodik-result${candidate.primary ? ' is-primary' : ''}`} key={candidate.kodikId}>
                                    <div className="kodik-result-main">
                                        <div className="kodik-result-title">
                                            <strong>{candidate.title || candidate.titleOriginal || candidate.kodikId}</strong>
                                            {candidate.primary ? <span className="kodik-primary-badge">Связано</span> : null}
                                            {!candidate.primary && !candidate.manualLocked && index === 0 ? <span className="kodik-suggested-badge">Лучшее совпадение</span> : null}
                                            <StatusBadge status={candidate.status} />
                                        </div>
                                        <span>{candidate.titleOriginal || 'Оригинальное название не указано'}</span>
                                        <code>{candidate.kodikId}</code>
                                    </div>
                                    <div className="kodik-result-meta">
                                        <span>Год: {formatValue(candidate.year)}</span>
                                        <span>Озвучка: <Translation translation={candidate.translation} /></span>
                                        <span>Shikimori: {formatValue(candidate.shikimoriId)}</span>
                                        <span>Уверенность: {formatValue(candidate.score)}</span>
                                        <span>Совпадения: {formatMatchMethod(candidate.method)}</span>
                                        {candidate.logicalSeason ? <span>Сезон Animix: {candidate.logicalSeason}</span> : null}
                                        {candidate.sourceSeason ? <span>Сезон Kodik: {candidate.sourceSeason}</span> : null}
                                    </div>
                                    <div className="kodik-result-actions">
                                        <button type="button" className="ui-btn ui-btn--sm admin-button" onClick={() => setManualMatch(candidate.kodikId)} disabled={busyKodikId === candidate.kodikId || candidate.manualLocked}>
                                            {candidate.manualLocked ? 'Связано' : 'Связать с Animix'}
                                        </button>
                                        <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => openPlayerPreview(candidate, 'candidates')} disabled={previewLoadingId === candidate.kodikId}>
                                            {previewLoadingId === candidate.kodikId ? 'Открываем…' : 'Проверить плеер'}
                                        </button>
                                        <details className="kodik-candidate-more">
                                            <summary>Ещё</summary>
                                            <div>
                                                {candidate.shikimoriId ? <button type="button" onClick={() => chooseCandidateShikimori(candidate)}>Связать через Shikimori</button> : null}
                                                <button type="button" onClick={() => preparePart(candidate)}>Добавить сегмент</button>
                                                {candidate.manualLocked ? <button type="button" className="is-danger" onClick={() => deleteManualMatch(candidate.kodikId)} disabled={busyKodikId === candidate.kodikId}>Удалить связь</button> : null}
                                            </div>
                                        </details>
                                    </div>
                                </article>
                            ))}
                            {candidatesData && !candidates.length ? <p className="kodik-empty">Кандидатов не найдено. Перейдите в ручной поиск.</p> : null}
                            {!candidatesData && !candidateLoading ? <p className="kodik-empty is-start">Выберите аниме в очереди или загрузите его по Animix ID.</p> : null}
                        </div>

                        <details className="kodik-shikimori-panel" ref={shikimoriPanelRef}>
                            <summary>
                                <span>Сопоставление по Shikimori ID</span>
                                <small>{candidatesData?.shikimoriId ? `Сохранён ${candidatesData.shikimoriId}` : 'Для спорных случаев'}</small>
                            </summary>
                            <div className="kodik-shikimori-body">
                                <div className="kodik-shikimori-controls">
                                    <label className="kodik-field">
                                        <span>Shikimori ID</span>
                                        <input value={shikimoriId} onChange={(event) => setShikimoriId(event.target.value)} inputMode="numeric" placeholder="16498" />
                                    </label>
                                    <div className="kodik-shikimori-modes" role="group" aria-label="Выбор перевода Kodik">
                                        <button type="button" className={shikimoriMode === 'auto' ? 'is-active' : ''} onClick={() => setShikimoriMode('auto')}>Автовыбор</button>
                                        <button type="button" className={shikimoriMode === 'explicit' ? 'is-active' : ''} onClick={() => setShikimoriMode('explicit')}>Указать Kodik ID</button>
                                    </div>
                                    {shikimoriMode === 'explicit' ? (
                                        <label className="kodik-field">
                                            <span>Kodik ID перевода</span>
                                            <input value={shikimoriKodikId} onChange={(event) => setShikimoriKodikId(event.target.value)} placeholder="serial-123456" />
                                        </label>
                                    ) : null}
                                    <button type="button" className="ui-btn ui-btn--sm admin-button" onClick={submitShikimoriMatch} disabled={shikimoriLoading}>Сопоставить</button>
                                </div>
                                {shikimoriResult ? (
                                    <div className={`kodik-shikimori-result${shikimoriResult.status === 'NOT_FOUND' ? ' is-warning' : ''}`}>
                                        <StatusBadge status={shikimoriResult.status} />
                                        <span>Shikimori ID {shikimoriResult.shikimoriId}</span>
                                        <span>{shikimoriResult.selectedKodikId ? `Выбран ${shikimoriResult.selectedKodikId}` : 'Точное совпадение не найдено'}</span>
                                        <span>Точных вариантов: {shikimoriResult.exactCandidateCount}</span>
                                    </div>
                                ) : null}
                            </div>
                        </details>
                    </section>
                </div>
            ) : null}

            {activeView === 'catalog' ? <section className="kodik-catalog-view" role="tabpanel">
                <div className="kodik-section-heading">
                    <div>
                        <h2>Все аниме в интеграции</h2>
                        <p>Откройте любую сущность, чтобы проверить основной источник, переводы и логические сезоны.</p>
                    </div>
                    <div className="kodik-catalog-total">
                        <strong>{catalogData?.total ?? '—'}</strong>
                        <span>аниме</span>
                    </div>
                </div>

                <form className="kodik-catalog-search" onSubmit={submitCatalogSearch}>
                    <label className="kodik-field">
                        <span>Название, Animix ID, Shikimori ID или Kodik ID</span>
                        <input value={catalogQuery} onChange={(event) => setCatalogQuery(event.target.value)} maxLength="200" placeholder="Например, One Piece или serial-52881" />
                    </label>
                    <button type="submit" className="ui-btn ui-btn--sm admin-button" disabled={catalogLoading}>Найти</button>
                    <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={resetCatalogFilters} disabled={catalogLoading}>Сбросить</button>
                </form>

                <div className="kodik-catalog-controls">
                    <div className="kodik-catalog-statuses" role="group" aria-label="Фильтр по статусу">
                        {CATALOG_STATUSES.map((status) => (
                            <button
                                type="button"
                                key={status}
                                className={catalogStatuses.includes(status) ? 'is-active' : ''}
                                aria-pressed={catalogStatuses.includes(status)}
                                onClick={() => toggleCatalogStatus(status)}
                            >
                                {STATUS_LABELS[status]}
                            </button>
                        ))}
                    </div>
                    <div className="kodik-catalog-sort">
                        <label>
                            <span>Сортировка</span>
                            <select value={catalogSort} onChange={(event) => {
                                setCatalogSort(event.target.value);
                                setCatalogPage(0);
                            }}>
                                {CATALOG_SORTS.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
                            </select>
                        </label>
                        <label>
                            <span>Порядок</span>
                            <select value={catalogDirection} onChange={(event) => {
                                setCatalogDirection(event.target.value);
                                setCatalogPage(0);
                            }}>
                                <option value="desc">По убыванию</option>
                                <option value="asc">По возрастанию</option>
                            </select>
                        </label>
                        <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => setCatalogReloadToken((value) => value + 1)} disabled={catalogLoading}>Обновить</button>
                    </div>
                </div>

                {catalogAppliedQuery ? <div className="kodik-catalog-query">Результаты по запросу: <strong>{catalogAppliedQuery}</strong></div> : null}
                {catalogError ? <p className="kodik-feedback is-error">{catalogError}</p> : null}

                <div className="kodik-catalog-list" aria-busy={catalogLoading}>
                    <div className="kodik-catalog-header" aria-hidden="true">
                        <span>Аниме</span>
                        <span>Состояние</span>
                        <span>Основной источник</span>
                        <span>Связи</span>
                        <span>Обновлено</span>
                        <span />
                    </div>
                    {catalogItems.map((item) => (
                        <article className="kodik-catalog-row" key={item.animixAnimeId}>
                            <div className="kodik-catalog-anime">
                                <img src={item.thumb || placeholderCover} onError={(event) => { event.currentTarget.src = placeholderCover; }} alt="" />
                                <div>
                                    <strong>{item.name || `Аниме ${item.animixAnimeId}`}</strong>
                                    <span>Animix ID {item.animixAnimeId} · {formatValue(item.parsedYear)} · {formatValue(item.studio)}</span>
                                    <small>Shikimori: {formatValue(item.shikimoriId)}</small>
                                </div>
                            </div>
                            <div className="kodik-catalog-state">
                                <StatusBadge status={item.status} />
                                {item.manualLocked ? <span className="kodik-lock-badge">Закреплено вручную</span> : null}
                            </div>
                            <div className="kodik-catalog-primary">
                                <code>{item.primaryKodikId || 'Источник не выбран'}</code>
                                <span><Translation translation={item.primaryTranslation} /></span>
                            </div>
                            <div className="kodik-catalog-counts">
                                <span><strong>{item.candidateCount ?? 0}</strong> кандидатов</span>
                                <span><strong>{item.partCount ?? 0}</strong> сезонов</span>
                            </div>
                            <time dateTime={item.updatedAt || undefined}>{formatDate(item.updatedAt)}</time>
                            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => openCatalogAnime(item)} disabled={candidateLoading}>Открыть</button>
                        </article>
                    ))}
                    {catalogLoading && !catalogItems.length ? <p className="kodik-empty">Загружаем каталог…</p> : null}
                    {!catalogLoading && !catalogItems.length && !catalogError ? <p className="kodik-empty">По выбранным условиям ничего не найдено.</p> : null}
                </div>

                <div className="kodik-catalog-pagination">
                    <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => changeCatalogPage(catalogPage - 1)} disabled={catalogLoading || catalogPage === 0}>Назад</button>
                    <span>Страница {catalogPage + 1} из {Math.max(catalogData?.totalPages || 1, 1)}</span>
                    <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => changeCatalogPage(catalogPage + 1)} disabled={catalogLoading || catalogPage + 1 >= (catalogData?.totalPages || 1)}>Дальше</button>
                </div>
            </section> : null}

            {activeView === 'parts' ? <section className="kodik-admin-section kodik-view-panel" ref={partsSectionRef} role="tabpanel">
                <div className="kodik-view-target is-with-reason">
                    <label className="kodik-field">
                        <span>Animix ID</span>
                        <input ref={animeIdInputRef} type="number" min="1" value={animeId} onChange={handleAnimeIdChange} placeholder="504" />
                    </label>
                    <div><strong>{candidatesData?.name || (animeId ? `Аниме ${animeId}` : 'Аниме не выбрано')}</strong><span>Настройка сегментов и нумерации серий</span></div>
                    <label className="kodik-field">
                        <span>Комментарий для истории</span>
                        <input value={reason} onChange={(event) => setReason(event.target.value)} maxLength="500" />
                    </label>
                    <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => {
                        const id = requireAnimeId(setPartsError);
                        if (id) Promise.all([loadPartsById(id), loadCandidatesById(id, false)]);
                    }} disabled={partsLoading}>Загрузить</button>
                </div>
                <div className="kodik-section-heading">
                    <div>
                        <h2>Сегменты Kodik</h2>
                        <p>Соберите логический сезон Animix из нескольких диапазонов Kodik или вставьте фильм как OVA.</p>
                    </div>
                    <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => {
                        const id = requireAnimeId(setPartsError);
                        if (id) loadPartsById(id);
                    }} disabled={partsLoading}>Обновить сегменты</button>
                </div>

                {partsError ? <p className="kodik-feedback is-error">{partsError}</p> : null}
                {partsNotice ? <p className="kodik-feedback is-success">{partsNotice}</p> : null}

                <div className="kodik-parts-list" aria-busy={partsLoading}>
                    {parts.map((part) => (
                        <article className="kodik-part" key={part.partId || `${part.season}-${part.kodikId}-${part.targetEpisodeStart}`}>
                            <div className="kodik-part-season">
                                <span>Сезон</span>
                                <strong>{part.season}</strong>
                            </div>
                            <div className="kodik-result-main">
                                <strong>{part.title || part.kodikId}</strong>
                                <code>{part.kodikId}</code>
                            </div>
                            <div className="kodik-result-meta">
                                <span>Сегмент #{formatValue(part.partId)}</span>
                                <span>Источник: сезон {formatValue(part.sourceSeason)}, серии {part.sourceEpisodeStart ? `${part.sourceEpisodeStart}–${part.sourceEpisodeEnd || 'до конца'}` : 'фильм'}</span>
                                <span>В Animix: серии {part.targetEpisodeStart === part.targetEpisodeEnd ? part.targetEpisodeStart : `${formatValue(part.targetEpisodeStart)}–${formatValue(part.targetEpisodeEnd)}`}</span>
                                <span>{part.type || 'Тип не указан'}</span>
                                <span>{part.episodeCount != null ? `${part.episodeCount} серий` : 'Количество серий неизвестно'}</span>
                                <span><Translation translation={part.translation} /></span>
                            </div>
                            <div className="kodik-result-actions">
                                <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => preparePart(part)}>Изменить</button>
                                <button type="button" className="ui-btn ui-btn--sm admin-danger-button" onClick={() => deletePart(part)} disabled={busyPartId === String(part.partId)}>Удалить</button>
                            </div>
                        </article>
                    ))}
                    {partsData && !parts.length ? <p className="kodik-empty">Сегменты ещё не настроены. Аниме работает по старой одиночной привязке.</p> : null}
                    {!partsData && !partsLoading ? <p className="kodik-empty">Укажите Animix ID и загрузите данные, чтобы увидеть настроенные сегменты.</p> : null}
                </div>

                <form className="kodik-part-editor" onSubmit={savePart}>
                    <div className="kodik-part-editor-heading">
                        <div>
                            <h3>{partForm.partId ? `Изменить сегмент #${partForm.partId}` : 'Добавить сегмент'}</h3>
                            <p>Укажите диапазон Kodik и номер, с которого он продолжится в Animix. Для фильма оставьте исходные серии пустыми.</p>
                        </div>
                        <span>Пересекающиеся клиентские номера API отклонит.</span>
                    </div>
                    <div className="kodik-form-grid is-parts">
                        <label className="kodik-field">
                            <span>Логический сезон Animix</span>
                            <input type="number" min="1" required value={partForm.logicalSeason} onChange={(event) => {
                                const logicalSeason = event.target.value;
                                setPartForm((current) => ({
                                    ...current,
                                    logicalSeason,
                                    targetEpisodeStart: current.targetEpisodeStart || String(getNextTargetEpisode(logicalSeason)),
                                }));
                            }} placeholder={String(getNextLogicalSeason())} />
                        </label>
                        <label className="kodik-field">
                            <span>Kodik ID части</span>
                            <input required value={partForm.kodikId} onChange={(event) => setPartForm((current) => ({ ...current, kodikId: event.target.value }))} placeholder="serial-part-2" />
                        </label>
                        <label className="kodik-field">
                            <span>Исходный сезон Kodik</span>
                            <input type="number" min="1" value={partForm.sourceSeason} onChange={(event) => setPartForm((current) => ({ ...current, sourceSeason: event.target.value }))} placeholder="Определить автоматически" />
                        </label>
                        <label className="kodik-field">
                            <span>Первая серия Kodik</span>
                            <input type="number" min="1" value={partForm.sourceEpisodeStart} onChange={(event) => setPartForm((current) => ({ ...current, sourceEpisodeStart: event.target.value }))} placeholder="Для фильма не указывать" />
                        </label>
                        <label className="kodik-field">
                            <span>Последняя серия Kodik</span>
                            <input type="number" min="1" value={partForm.sourceEpisodeEnd} onChange={(event) => setPartForm((current) => ({ ...current, sourceEpisodeEnd: event.target.value }))} placeholder="Пусто — до конца" />
                        </label>
                        <label className="kodik-field">
                            <span>Начать в Animix с серии</span>
                            <input type="number" min="1" required value={partForm.targetEpisodeStart} onChange={(event) => setPartForm((current) => ({ ...current, targetEpisodeStart: event.target.value }))} placeholder={String(getNextTargetEpisode(partForm.logicalSeason || getNextLogicalSeason()))} />
                        </label>
                    </div>
                    {partMappingPreview ? <div className="kodik-mapping-preview">{partMappingPreview}</div> : null}
                    {availableSourceSeasons.length ? (
                        <div className="kodik-source-season-options">
                            <span>Доступные исходные сезоны:</span>
                            {availableSourceSeasons.map((season) => (
                                <button type="button" key={season} className={String(season) === partForm.sourceSeason ? 'is-active' : ''} onClick={() => setPartForm((current) => ({ ...current, sourceSeason: String(season) }))}>{season}</button>
                            ))}
                        </div>
                    ) : null}
                    <div className="kodik-inline-actions">
                        <button type="submit" className="ui-btn ui-btn--sm admin-button" disabled={Boolean(busyPartId)}>{partForm.partId ? 'Сохранить изменения' : 'Добавить сегмент'}</button>
                        <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => {
                            setPartForm(EMPTY_PART_FORM);
                            setAvailableSourceSeasons([]);
                            setPartsError('');
                        }}>Очистить</button>
                    </div>
                </form>
            </section> : null}

            {activeView === 'search' ? <section className="kodik-admin-section kodik-view-panel" role="tabpanel">
                <div className="kodik-view-target">
                    <label className="kodik-field">
                        <span>Animix ID для связи</span>
                        <input ref={animeIdInputRef} type="number" min="1" value={animeId} onChange={handleAnimeIdChange} placeholder="504" />
                    </label>
                    <div><strong>{candidatesData?.name || (animeId ? `Аниме ${animeId}` : 'Сначала выберите аниме')}</strong><span>Результат поиска будет связан с этим Animix ID</span></div>
                    <label className="kodik-field">
                        <span>Комментарий для истории</span>
                        <input value={reason} onChange={(event) => setReason(event.target.value)} maxLength="500" />
                    </label>
                </div>
                <div className="kodik-section-heading">
                    <div>
                        <h2>Ручной поиск в Kodik</h2>
                        <p>Поиск по всему каталогу, когда правильного материала нет среди кандидатов.</p>
                    </div>
                </div>
                <form onSubmit={runSearch}>
                    <div className="kodik-form-grid">
                        {PRIMARY_SEARCH_FIELDS.map(([key, label]) => (
                            <label className="kodik-field" key={key}>
                                <span>{label}</span>
                                <input value={searchForm[key] || ''} onChange={(event) => updateSearch(key, event.target.value)} />
                            </label>
                        ))}
                    </div>
                    <details className="kodik-advanced-search">
                        <summary>Дополнительные фильтры</summary>
                        <div className="kodik-form-grid">
                            {ADVANCED_SEARCH_FIELDS.map(([key, label]) => (
                                <label className="kodik-field" key={key}>
                                    <span>{label}</span>
                                    <input value={searchForm[key] || ''} onChange={(event) => updateSearch(key, event.target.value)} />
                                </label>
                            ))}
                            <label className="kodik-field">
                                <span>Максимум результатов</span>
                                <input type="number" min="1" max="100" value={searchForm.limit} onChange={(event) => updateSearch('limit', event.target.value)} />
                            </label>
                        </div>
                        <div className="kodik-checkboxes">
                            {BOOLEAN_SEARCH_FIELDS.map(([key, label]) => (
                                <label key={key}>
                                    <input type="checkbox" checked={Boolean(searchForm[key])} onChange={(event) => updateSearch(key, event.target.checked)} />
                                    <span>{label}</span>
                                </label>
                            ))}
                        </div>
                    </details>
                    <div className="kodik-inline-actions">
                        <button type="submit" className="ui-btn ui-btn--sm admin-button" disabled={searchLoading}>Искать в Kodik</button>
                        <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => setSearchForm(initialSearch)}>Очистить поля</button>
                        {searchData ? <span className="kodik-result-count">Найдено: {searchData.total ?? searchItems.length}</span> : null}
                    </div>
                </form>
                {searchError ? <p className="kodik-feedback is-error">{searchError}</p> : null}
                {searchNotice ? <p className="kodik-feedback is-success">{searchNotice}</p> : null}
                <div className="kodik-results">
                    {searchItems.map((item) => (
                        <article className="kodik-result" key={item.kodikId}>
                            <div className="kodik-result-main">
                                <strong>{item.title || item.titleOriginal || item.kodikId}</strong>
                                <span>{item.titleOriginal || item.type || 'Без дополнительного названия'}</span>
                                <code>{item.kodikId}</code>
                            </div>
                            <div className="kodik-result-meta">
                                <span>{formatValue(item.year)}</span>
                                <Translation translation={item.translation} />
                                <span>Shikimori ID: {formatValue(item.shikimoriId)}</span>
                                <span>{item.quality || 'Качество не указано'}</span>
                                <span>{item.lastSeason ? `${item.lastSeason} сезон` : 'Фильм'}</span>
                                <span>{item.lastEpisode ? `${item.lastEpisode} серий` : formatValue(item.type)}</span>
                                {item.blockedCountries?.length ? <span>Блокировки: {item.blockedCountries.join(', ')}</span> : null}
                            </div>
                            <div className="kodik-result-actions">
                                <button type="button" className="ui-btn ui-btn--sm admin-button" onClick={() => setManualMatch(item.kodikId, 'search')} disabled={busyKodikId === item.kodikId}>
                                    {animeId ? `Связать с ID ${animeId}` : 'Указать Animix ID и связать'}
                                </button>
                                {item.shikimoriId ? <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => chooseCandidateShikimori(item)}>Подставить Shikimori</button> : null}
                                <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => preparePart(item)}>Добавить сегмент</button>
                                <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => openPlayerPreview(item)} disabled={previewLoadingId === item.kodikId}>
                                    {previewLoadingId === item.kodikId ? 'Открываем…' : 'Проверить плеер'}
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            </section> : null}

            {activeView === 'operations' ? <section className="kodik-admin-section kodik-view-panel" role="tabpanel">
                <div className="kodik-section-heading">
                    <div>
                        <h2>Обслуживание каталога</h2>
                        <p>Эти операции не нужны для обычного ручного сопоставления.</p>
                    </div>
                </div>
                {operationsError ? <p className="kodik-feedback is-error">{operationsError}</p> : null}
                <div className="kodik-operations-grid">
                    <div className="kodik-operation">
                        <h3>Автоподбор для одного аниме</h3>
                        <p className="kodik-operation-description">Повторно рассчитает кандидатов. Ручную закреплённую связь не перезапишет.</p>
                        <label className="kodik-field">
                            <span>Animix ID</span>
                            <input ref={animeIdInputRef} type="number" min="1" value={animeId} onChange={handleAnimeIdChange} placeholder="504" />
                        </label>
                        <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={startRematch} disabled={rematchLoading}>Запустить автоподбор</button>
                        {rematch ? (
                            <dl className="kodik-operation-status">
                                <dt>Статус</dt><dd><StatusBadge status={rematch.status} /></dd>
                                <dt>Результат</dt><dd>{formatValue(rematch.outcome)}</dd>
                                <dt>Kodik ID</dt><dd>{formatValue(rematch.primaryKodikId)}</dd>
                                <dt>Ошибка</dt><dd>{formatValue(rematch.errorMessage)}</dd>
                            </dl>
                        ) : null}
                        <details className="kodik-technical-details">
                            <summary>Проверить другую задачу по ID</summary>
                            <label className="kodik-field">
                                <span>Task ID</span>
                                <input value={rematchId} onChange={(event) => setRematchId(event.target.value)} placeholder="UUID задачи" />
                            </label>
                            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={refreshRematch} disabled={rematchLoading || !rematchId}>Проверить</button>
                        </details>
                    </div>
                    <div className="kodik-operation">
                        <h3>Синхронизация каталога</h3>
                        <p className="kodik-operation-description">Загружает актуальные материалы и серии из Kodik в локальный каталог интеграции.</p>
                        <div className="kodik-sync-modes" role="group" aria-label="Режим синхронизации">
                            {SYNC_MODES.map((mode) => (
                                <button type="button" key={mode.value} className={syncMode === mode.value ? 'is-active' : ''} onClick={() => setSyncMode(mode.value)}>{mode.label}</button>
                            ))}
                        </div>
                        <p className="kodik-mode-description"><strong>{selectedSyncMode.label}.</strong> {selectedSyncMode.description}</p>
                        <button type="button" className="ui-btn ui-btn--sm admin-button" onClick={startSync} disabled={syncLoading}>Запустить выбранное обновление</button>
                        {syncRun ? (
                            <dl className="kodik-operation-status is-counters">
                                <dt>Статус</dt><dd><StatusBadge status={syncRun.status} /></dd>
                                <dt>Получено</dt><dd>{formatValue(syncRun.fetchedCount)}</dd>
                                <dt>Создано</dt><dd>{formatValue(syncRun.createdCount)}</dd>
                                <dt>Обновлено</dt><dd>{formatValue(syncRun.updatedCount)}</dd>
                                <dt>Сопоставлено</dt><dd>{formatValue(syncRun.matchedCount)}</dd>
                                <dt>Неоднозначно</dt><dd>{formatValue(syncRun.ambiguousCount)}</dd>
                                <dt>Ошибки</dt><dd>{formatValue(syncRun.failedCount)}</dd>
                            </dl>
                        ) : null}
                        <details className="kodik-technical-details">
                            <summary>Проверить другой запуск по ID</summary>
                            <label className="kodik-field">
                                <span>Run ID</span>
                                <input value={syncRunId} onChange={(event) => setSyncRunId(event.target.value)} placeholder="UUID запуска" />
                            </label>
                            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={refreshSync} disabled={syncLoading || !syncRunId}>Проверить</button>
                        </details>
                    </div>
                </div>
            </section> : null}
            {playerPreview ? (
                <div className="kodik-player-modal" role="presentation" onMouseDown={() => setPlayerPreview(null)}>
                    <div className="kodik-player-dialog" role="dialog" aria-modal="true" aria-labelledby="kodik-player-modal-title" onMouseDown={(event) => event.stopPropagation()}>
                        <header>
                            <div>
                                <strong id="kodik-player-modal-title">{playerPreview.title}</strong>
                                <code>{playerPreview.kodikId}</code>
                            </div>
                            <button type="button" className="kodik-player-close ui-close ui-close--sm ui-close--round" onClick={() => setPlayerPreview(null)} aria-label="Закрыть плеер" title="Закрыть">×</button>
                        </header>
                        <iframe
                            src={playerPreview.url}
                            title={`Kodik: ${playerPreview.title}`}
                            allow="autoplay *; fullscreen *"
                            allowFullScreen
                        />
                    </div>
                </div>
            ) : null}
        </AdminLayout>
    );
};

export default KodikModerationPage;
